import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { registerCollaborationBridge, useEditorStore } from "./editor-store";
import type { EditorStoreState } from "./editor-store";
import { useCollaborationStore } from "../collaboration/collaboration-store";
import { useAuthStore } from "../auth/auth-store";
import { socketClient } from "@/services/websocket/socketClient";
import { responsivePositionTranslator } from "@/services/responsive/ResponsivePositionTranslator";
import {
  Operation,
  OperationType,
  Component,
  ComponentInput,
  Position,
  ResponsivePosition,
  Size,
} from "@/types";
import { generateId, isRecord, logger } from "@/utils";
import { deepMergeBreakpoints } from "@/utils/deepMergeBreakpoints";

interface OperationMetadata {
  optimistic: boolean;
  acknowledged: boolean;
  retryCount: number;
  createdAt: Date;
}

type CreateOperationPayload = Partial<
  Pick<
    Operation,
    | "componentId"
    | "componentData"
    | "field"
    | "value"
    | "oldValue"
    | "position"
    | "length"
    | "data"
  >
>;

function hasOwnKey<T extends object>(
  value: T,
  key: PropertyKey,
): key is keyof T {
  return Object.prototype.hasOwnProperty.call(value, key);
}

function isPositionValue(value: unknown): value is Position {
  return (
    isRecord(value) &&
    typeof value.x === "number" &&
    typeof value.y === "number" &&
    (value.z === undefined || typeof value.z === "number")
  );
}

function isSizeValue(value: unknown): value is Size {
  return (
    isRecord(value) &&
    (typeof value.width === "number" || typeof value.width === "string") &&
    (typeof value.height === "number" || typeof value.height === "string")
  );
}

function isResponsivePositionValue(
  value: unknown,
): value is ResponsivePosition {
  return (
    isRecord(value) &&
    typeof value.strategy === "string" &&
    (value.breakpoints === undefined || isRecord(value.breakpoints))
  );
}

function isComponentValue(value: unknown): value is Component {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    typeof value.projectId === "string" &&
    typeof value.pageId === "string" &&
    typeof value.name === "string" &&
    typeof value.type === "string" &&
    isRecord(value.properties) &&
    (value.position === undefined || isPositionValue(value.position)) &&
    isSizeValue(value.size) &&
    isRecord(value.style) &&
    isResponsivePositionValue(value.responsivePosition) &&
    Array.isArray(value.children) &&
    typeof value.order === "number" &&
    typeof value.locked === "boolean" &&
    typeof value.hidden === "boolean" &&
    typeof value.version === "number" &&
    typeof value.createdBy === "string" &&
    typeof value.lastModifiedBy === "string" &&
    value.lastModifiedAt instanceof Date &&
    typeof value.isDeleted === "boolean" &&
    value.createdAt instanceof Date &&
    value.updatedAt instanceof Date
  );
}

function mergeResponsivePosition(
  existing: ResponsivePosition,
  incoming: ResponsivePosition,
): ResponsivePosition {
  return {
    ...existing,
    ...incoming,
    breakpoints: deepMergeBreakpoints(existing.breakpoints, incoming.breakpoints),
  };
}

interface CollaborationEditorBridgeState {
  // Operation tracking
  pendingOperations: Map<
    string,
    { operation: Operation; metadata: OperationMetadata }
  >;
  operationQueue: Operation[];
  isProcessing: boolean;

  // Collaboration state
  isCollaborationActive: boolean;
  currentProjectId: string | null;

  // Throttling and batching
  throttleSettings: {
    batchDelay: number;
    maxBatchSize: number;
    cursorThrottle: number;
  };

  // Actions
  initializeCollaboration: (projectId: string) => Promise<void>;
  terminateCollaboration: () => Promise<void>;

  // Operation generation
  createOperation: (
    type: OperationType,
    data: CreateOperationPayload,
  ) => Operation;
  sendOperation: (operation: Operation, optimistic?: boolean) => Promise<void>;
  batchSendOperations: (operations: Operation[]) => Promise<void>;

  // Editor action interceptors
  interceptComponentAdd: (component: ComponentInput) => void;
  interceptComponentUpdate: (id: string, updates: Partial<Component>) => void;
  interceptComponentDelete: (id: string) => void;
  interceptComponentMove: (id: string, position: Position) => void;
  interceptComponentResize: (id: string, size: Size) => void;

  // Selection and cursor
  sendCursorUpdate: (position: Position) => void;
  sendSelectionUpdate: (componentIds: string[]) => void;

  // Operation acknowledgment
  acknowledgeOperation: (
    operationId: string,
    transformedOperation?: Operation,
  ) => void;
  handleOperationFailure: (operationId: string, error: string) => void;

  // Conflict resolution
  handleRemoteOperation: (operation: Operation) => void;
  applyOperationToEditor: (
    operation: Operation,
    editorStore: EditorStoreState,
  ) => void;
  revertOptimisticOperation: (operation: Operation) => void;
  resolveOperationConflict: (
    localOp: Operation,
    remoteOp: Operation,
  ) => Operation | null;

  // Utility
  getOperationStatus: (
    operationId: string,
  ) => "pending" | "acknowledged" | "failed" | null;
  clearOldOperations: () => void;
  retryFailedOperations: () => void;

  // Operational Transform validation
  validateOperationIntegrity: (operation: Operation) => boolean;
  detectConcurrentOperations: (operation: Operation) => Operation[];
  resolveConcurrentOperations: (operations: Operation[]) => Operation[];
}

function buildCollaborativeComponent(
  componentData: ComponentInput,
  currentProjectId: string | null,
  currentPageId: string | null,
): Component {
  const now = new Date();

  return {
    ...componentData,
    id: componentData.id ?? generateId("component"),
    projectId: componentData.projectId ?? currentProjectId ?? "",
    pageId: componentData.pageId ?? currentPageId ?? "",
    properties: componentData.properties ?? {},
    size: componentData.size ?? { width: 300, height: 200 },
    style: componentData.style ?? {},
    responsivePosition:
      componentData.responsivePosition ??
      responsivePositionTranslator.generateDefaultResponsivePosition(
        componentData.type,
      ),
    children: componentData.children ?? [],
    order: componentData.order ?? 0,
    locked: componentData.locked ?? false,
    hidden: componentData.hidden ?? false,
    version: componentData.version ?? 1,
    createdBy: componentData.createdBy ?? "system",
    lastModifiedBy: componentData.lastModifiedBy ?? "system",
    lastModifiedAt: componentData.lastModifiedAt ?? now,
    isDeleted: componentData.isDeleted ?? false,
    createdAt: componentData.createdAt ?? now,
    updatedAt: componentData.updatedAt ?? now,
  } satisfies Component;
}

export const useCollaborationEditorBridge =
  create<CollaborationEditorBridgeState>()(
    subscribeWithSelector(
      immer((set, get) => ({
        // Initial state
        pendingOperations: new Map(),
        operationQueue: [],
        isProcessing: false,
        isCollaborationActive: false,
        currentProjectId: null,

        throttleSettings: {
          batchDelay: 100,
          maxBatchSize: 10,
          cursorThrottle: 60,
        },

        // Initialize collaboration
        initializeCollaboration: async (projectId: string) => {
          const collaboration = useCollaborationStore.getState();
          const auth = useAuthStore.getState();

          if (!auth.user) {
            throw new Error("User not authenticated");
          }

          try {
            // Initialize collaboration session
            await collaboration.initializeSession(
              `session_${projectId}`,
              projectId,
              auth.user,
            );

            set((state) => {
              state.isCollaborationActive = true;
              state.currentProjectId = projectId;
            });

            logger.info("Collaboration bridge initialized", { projectId });
          } catch (error) {
            logger.error("Failed to initialize collaboration bridge", error);
            throw error;
          }
        },

        terminateCollaboration: async () => {
          const collaboration = useCollaborationStore.getState();

          try {
            await collaboration.terminateSession();

            set((state) => {
              state.isCollaborationActive = false;
              state.currentProjectId = null;
              state.pendingOperations.clear();
              state.operationQueue = [];
            });

            logger.info("Collaboration bridge terminated");
          } catch (error) {
            logger.error("Failed to terminate collaboration bridge", error);
          }
        },

        // Create operation
        createOperation: (
          type: OperationType,
          data: CreateOperationPayload,
        ): Operation => {
          const auth = useAuthStore.getState();
          const collaboration = useCollaborationStore.getState();

          if (!auth.user) {
            throw new Error("User not authenticated");
          }

          const operationId = generateId("op");
          const timestamp = new Date();
          const vectorClock = collaboration.incrementVectorClock(auth.user.id);

          const operation: Operation = {
            id: operationId,
            type,
            userId: auth.user.id,
            timestamp,
            vector: vectorClock,
            dependencies: [],
            ...data,
          };

          return operation;
        },

        // Send operation
        sendOperation: async (operation: Operation, optimistic = true) => {
          const { isCollaborationActive, pendingOperations } = get();

          if (!isCollaborationActive) {
            logger.warn("Collaboration not active, operation not sent");
            return;
          }

          try {
            // Store as pending
            set((state) => {
              state.pendingOperations.set(operation.id, {
                operation,
                metadata: {
                  optimistic,
                  acknowledged: false,
                  retryCount: 0,
                  createdAt: new Date(),
                },
              });
            });

            // Apply optimistically if requested
            if (optimistic) {
              get().handleRemoteOperation(operation);
            }

            // Send via WebSocket
            socketClient.sendOperation(operation);

            // Set timeout for acknowledgment
            setTimeout(() => {
              const pending = get().pendingOperations.get(operation.id);
              if (pending && !pending.metadata.acknowledged) {
                get().handleOperationFailure(
                  operation.id,
                  "Acknowledgment timeout",
                );
              }
            }, 5000);

            logger.debug("Operation sent", {
              operationId: operation.id,
              type: operation.type,
            });
          } catch (error) {
            logger.error("Failed to send operation", {
              operationId: operation.id,
              error,
            });
            get().handleOperationFailure(operation.id, error instanceof Error ? error.message : String(error));
          }
        },

        // Batch send operations
        batchSendOperations: async (operations: Operation[]) => {
          const { isCollaborationActive, throttleSettings } = get();

          if (!isCollaborationActive || operations.length === 0) return;

          // Process operations in batches to avoid overwhelming the server
          const batches = [];
          for (
            let i = 0;
            i < operations.length;
            i += throttleSettings.maxBatchSize
          ) {
            batches.push(
              operations.slice(i, i + throttleSettings.maxBatchSize),
            );
          }

          for (const batch of batches) {
            const promises = batch.map((op) => get().sendOperation(op, false));
            await Promise.all(promises);

            // Add delay between batches to prevent server overload
            if (batches.length > 1) {
              await new Promise((resolve) =>
                setTimeout(resolve, throttleSettings.batchDelay),
              );
            }
          }
        },

        // Intercept component add
        interceptComponentAdd: (componentData) => {
          const {
            isCollaborationActive,
            createOperation,
            sendOperation,
            currentProjectId,
          } = get();

          if (!isCollaborationActive) return;

          const editorStore = useEditorStore.getState();
          const component = buildCollaborativeComponent(
            componentData,
            currentProjectId ?? editorStore.project?.id ?? null,
            editorStore.currentPageId ?? componentData.pageId ?? null,
          );

          const operation = createOperation(OperationType.CREATE_COMPONENT, {
            componentId: component.id,
            componentData: component,
          });

          sendOperation(operation);
        },

        // Intercept component update
        interceptComponentUpdate: (id: string, updates: Partial<Component>) => {
          const { isCollaborationActive, createOperation, sendOperation } =
            get();

          if (!isCollaborationActive) return;

          // Get current component for oldValue
          const editorStore = useEditorStore.getState();
          const currentComponent = editorStore.getComponent(id);

          if (!currentComponent) {
            logger.warn("Component not found for update", { id });
            return;
          }

          // Eager-merge responsivePosition.breakpoints before decomposing into operations
          const mergedUpdates: Partial<Component> = updates.responsivePosition
            ? {
                ...updates,
                responsivePosition: mergeResponsivePosition(
                  currentComponent.responsivePosition,
                  updates.responsivePosition,
                ),
              }
            : { ...updates };

          // Create operations for each field change (using merged values)
          Object.entries(mergedUpdates).forEach(([field, newValue]) => {
            if (field === "updatedAt") return; // Skip timestamp updates
            if (!hasOwnKey(currentComponent, field)) return;

            const oldValue = currentComponent[field];

            if (oldValue !== newValue) {
              const operation = createOperation(OperationType.UPDATE, {
                componentId: id,
                field,
                value: newValue,
                oldValue,
              });

              sendOperation(operation);
            }
          });

          // Apply merged state locally (since updateComponent returned early at the short-circuit)
          useEditorStore.setState((state) => {
            const index = state.components.findIndex((c) => c.id === id);
            if (index !== -1) {
              const existing = state.components[index];
              const localMergedProperties = mergedUpdates.properties
                ? { ...existing.properties, ...mergedUpdates.properties }
                : existing.properties;
              const localMergedStyle = mergedUpdates.style
                ? { ...existing.style, ...mergedUpdates.style }
                : existing.style;
              const nextResponsivePosition =
                mergedUpdates.responsivePosition ?? existing.responsivePosition;

              state.components[index] = {
                ...existing,
                ...mergedUpdates,
                properties: localMergedProperties,
                style: localMergedStyle,
                responsivePosition: nextResponsivePosition,
                updatedAt: new Date(),
              };
              state.componentMap.set(id, state.components[index]);
              state.isModified = true;
              state.lastUpdateTimestamp = Date.now();
            }
          });
        },

        // Intercept component delete
        interceptComponentDelete: (id: string) => {
          const { isCollaborationActive, createOperation, sendOperation } =
            get();

          if (!isCollaborationActive) return;

          // Get component data before deletion for potential undo
          const editorStore = useEditorStore.getState();
          const component = editorStore.getComponent(id);

          const operation = createOperation(OperationType.DELETE_COMPONENT, {
            componentId: id,
            oldValue: component,
          });

          sendOperation(operation);
        },

        // Intercept component move
        interceptComponentMove: (id: string, position: Position) => {
          const { isCollaborationActive, createOperation, sendOperation } =
            get();

          if (!isCollaborationActive) return;

          const editorStore = useEditorStore.getState();
          const component = editorStore.getComponent(id);

          if (!component) return;

          const operation = createOperation(OperationType.MOVE, {
            componentId: id,
            value: position,
            oldValue: component.position,
          });

          sendOperation(operation);
        },

        // Intercept component resize
        interceptComponentResize: (id: string, size: Size) => {
          const { isCollaborationActive, createOperation, sendOperation } =
            get();

          if (!isCollaborationActive) return;

          const editorStore = useEditorStore.getState();
          const component = editorStore.getComponent(id);

          if (!component) return;

          const operation = createOperation(OperationType.UPDATE, {
            componentId: id,
            field: "size",
            value: size,
            oldValue: component.size,
          });

          sendOperation(operation);
        },

        // Send cursor update (throttled)
        sendCursorUpdate: (() => {
          let lastUpdate = 0;
          let throttleTimer: NodeJS.Timeout | null = null;
          let pendingPosition: Position | null = null;

          return (position: Position) => {
            const { isCollaborationActive, throttleSettings } = get();

            if (!isCollaborationActive) return;

            const now = Date.now();
            pendingPosition = position;

            if (now - lastUpdate >= throttleSettings.cursorThrottle) {
              socketClient.sendCursorUpdate(position, false);
              lastUpdate = now;
              pendingPosition = null;
            } else {
              if (throttleTimer) {
                clearTimeout(throttleTimer);
              }

              throttleTimer = setTimeout(
                () => {
                  if (pendingPosition) {
                    socketClient.sendCursorUpdate(pendingPosition, false);
                    lastUpdate = Date.now();
                    pendingPosition = null;
                  }
                },
                throttleSettings.cursorThrottle - (now - lastUpdate),
              );
            }
          };
        })(),

        // Send selection update
        sendSelectionUpdate: (componentIds: string[]) => {
          const { isCollaborationActive } = get();

          if (!isCollaborationActive) return;

          socketClient.sendSelectionUpdate(componentIds);
        },

        // Acknowledge operation
        acknowledgeOperation: (
          operationId: string,
          transformedOperation?: Operation,
        ) => {
          set((state) => {
            const pending = state.pendingOperations.get(operationId);
            if (pending) {
              pending.metadata.acknowledged = true;

              // If operation was transformed, apply the transformation
              if (transformedOperation && pending.metadata.optimistic) {
                // Revert optimistic operation and apply transformed one
                get().revertOptimisticOperation(pending.operation);
                get().handleRemoteOperation(transformedOperation);
              }

              // Clean up after delay
              setTimeout(() => {
                state.pendingOperations.delete(operationId);
              }, 30000);
            }
          });

          logger.debug("Operation acknowledged", { operationId });
        },

        // Handle operation failure
        handleOperationFailure: (operationId: string, error: string) => {
          set((state) => {
            const pending = state.pendingOperations.get(operationId);
            if (pending) {
              pending.metadata.retryCount++;

              // Revert optimistic operation
              if (pending.metadata.optimistic) {
                get().revertOptimisticOperation(pending.operation);
              }

              // Retry logic
              if (pending.metadata.retryCount < 3) {
                setTimeout(() => {
                  get().sendOperation(pending.operation, false);
                }, 1000 * pending.metadata.retryCount);
              } else {
                // Give up after 3 retries
                state.pendingOperations.delete(operationId);
                logger.error("Operation failed permanently", {
                  operationId,
                  error,
                });
              }
            }
          });
        },

        // Handle remote operation
        handleRemoteOperation: (operation: Operation) => {
          const editorStore = useEditorStore.getState();

          // Validate operation integrity first
          if (!get().validateOperationIntegrity(operation)) {
            logger.error("Invalid operation received, discarding", {
              operation,
            });
            return;
          }

          // Detect and resolve concurrent operations
          const concurrentOps = get().detectConcurrentOperations(operation);
          if (concurrentOps.length > 0) {
            const allOps = [operation, ...concurrentOps];
            const resolvedOps = get().resolveConcurrentOperations(allOps);

            // Apply resolved operations in order
            resolvedOps.forEach((resolvedOp) => {
              get().applyOperationToEditor(resolvedOp, editorStore);
            });

            return;
          }

          // Apply single operation
          get().applyOperationToEditor(operation, editorStore);
        },

        // Apply operation to editor (extracted for reuse)
        applyOperationToEditor: (
          operation: Operation,
          editorStore: EditorStoreState,
        ) => {
          try {
            switch (operation.type) {
              case OperationType.CREATE_COMPONENT:
                if (isComponentValue(operation.componentData)) {
                  // Apply component creation directly to editor
                  editorStore.setComponents([
                    ...editorStore.components,
                    operation.componentData,
                  ]);
                }
                break;

              case OperationType.UPDATE:
                if (
                  operation.componentId &&
                  operation.field &&
                  operation.value !== undefined
                ) {
                  const componentUpdate: Partial<Component> = {};
                  Reflect.set(componentUpdate, operation.field, operation.value);
                  editorStore.updateComponent(operation.componentId, {
                    ...componentUpdate,
                  }, { mergeBreakpoints: false });
                }
                break;

              case OperationType.DELETE_COMPONENT:
                if (operation.componentId) {
                  editorStore.deleteComponent(operation.componentId);
                }
                break;

              case OperationType.MOVE:
                if (operation.componentId && isPositionValue(operation.value)) {
                  editorStore.moveComponent(
                    operation.componentId,
                    operation.value,
                  );
                }
                break;

              default:
                logger.warn("Unknown operation type", { type: operation.type });
            }
          } catch (error) {
            logger.error("Failed to apply remote operation", {
              operation,
              error: error instanceof Error ? error.message : String(error),
            });
          }
        },

        // Revert optimistic operation
        revertOptimisticOperation: (operation: Operation) => {
          const editorStore = useEditorStore.getState();

          try {
            switch (operation.type) {
              case OperationType.CREATE_COMPONENT:
                if (operation.componentId) {
                  editorStore.deleteComponent(operation.componentId);
                }
                break;

              case OperationType.UPDATE:
                if (
                  operation.componentId &&
                  operation.field &&
                  operation.oldValue !== undefined
                ) {
                  editorStore.updateComponent(operation.componentId, {
                    [operation.field]: operation.oldValue,
                  });
                }
                break;

              case OperationType.DELETE_COMPONENT:
                if (isComponentValue(operation.componentData)) {
                  editorStore.setComponents([
                    ...editorStore.components,
                    operation.componentData,
                  ]);
                }
                break;

              case OperationType.MOVE:
                if (operation.componentId && isPositionValue(operation.oldValue)) {
                  editorStore.moveComponent(
                    operation.componentId,
                    operation.oldValue,
                  );
                }
                break;
            }
          } catch (error) {
            logger.error("Failed to revert optimistic operation", {
              operation,
              error: error instanceof Error ? error.message : String(error),
            });
          }
        },

        // Resolve operation conflict
        resolveOperationConflict: (
          localOp: Operation,
          remoteOp: Operation,
        ): Operation | null => {
          const collaborationStore = useCollaborationStore.getState();

          // Check if this creates a conflict that needs visual resolution
          if (
            localOp.componentId === remoteOp.componentId &&
            localOp.type === remoteOp.type &&
            localOp.field === remoteOp.field
          ) {
            // Create drag conflict for visual resolution
            const conflictId = collaborationStore.createDragConflict(
              localOp.componentId || "unknown",
              [localOp.userId, remoteOp.userId],
            );

            // Store operations for conflict resolution
            const conflictChoice = {
              type: "user_choice" as const,
              conflictId,
              description: `Conflicting ${localOp.type} operations on ${localOp.field || "component"}`,
              options: [
                {
                  label: "Keep My Changes",
                  action: () => {
                    collaborationStore.resolveDragConflict(
                      conflictId,
                      "reject",
                    );
                    return localOp;
                  },
                  variant: "primary" as const,
                },
                {
                  label: "Accept Their Changes",
                  action: () => {
                    collaborationStore.resolveDragConflict(conflictId, "merge");
                    return remoteOp;
                  },
                  variant: "secondary" as const,
                },
                {
                  label: "Manual Merge",
                  action: () => {
                    // This will trigger the MergeEditor UI
                    collaborationStore.resolveDragConflict(conflictId, "queue");
                    return null;
                  },
                  variant: "destructive" as const,
                },
              ],
              autoResolveIn: 15, // 15 seconds to choose
            };

            collaborationStore.addConflictChoice(conflictChoice);

            logger.info("Visual conflict resolution triggered", {
              conflictId,
              localOp: localOp.id,
              remoteOp: remoteOp.id,
            });

            // Return null to pause operation application until user resolves
            return null;
          }

          // For non-conflicting concurrent operations, use backend operational transform
          logger.info("Using operational transform for concurrent operations", {
            localOp: localOp.id,
            remoteOp: remoteOp.id,
          });

          // Simple priority-based resolution (could be enhanced with actual OT)
          if (localOp.userId.localeCompare(remoteOp.userId) > 0) {
            return localOp;
          } else {
            return remoteOp;
          }
        },

        // Get operation status
        getOperationStatus: (operationId: string) => {
          const pending = get().pendingOperations.get(operationId);
          if (!pending) return null;

          if (pending.metadata.acknowledged) return "acknowledged";
          if (pending.metadata.retryCount > 0) return "failed";
          return "pending";
        },

        // Clear old operations
        clearOldOperations: () => {
          set((state) => {
            const now = Date.now();
            const maxAge = 5 * 60 * 1000; // 5 minutes

            for (const [
              id,
              { metadata },
            ] of state.pendingOperations.entries()) {
              if (now - (new Date(metadata.createdAt)).getTime() > maxAge) {
                state.pendingOperations.delete(id);
              }
            }
          });
        },

        // Retry failed operations
        retryFailedOperations: () => {
          const { pendingOperations } = get();

          for (const [
            id,
            { operation, metadata },
          ] of pendingOperations.entries()) {
            if (
              !metadata.acknowledged &&
              metadata.retryCount > 0 &&
              metadata.retryCount < 3
            ) {
              get().sendOperation(operation, false);
            }
          }
        },

        // Validate operation integrity
        validateOperationIntegrity: (operation: Operation): boolean => {
          try {
            // Basic structure validation
            if (
              !operation.id ||
              !operation.type ||
              !operation.userId ||
              !operation.timestamp
            ) {
              logger.warn("Operation missing required fields", { operation });
              return false;
            }

            // Vector clock validation
            if (!operation.vector || typeof operation.vector !== "object") {
              logger.warn("Operation missing or invalid vector clock", {
                operation,
              });
              return false;
            }

            // Type-specific validation
            switch (operation.type) {
              case OperationType.CREATE_COMPONENT:
                if (!operation.componentData || !operation.componentId) {
                  logger.warn("CREATE_COMPONENT operation missing data", {
                    operation,
                  });
                  return false;
                }
                break;

              case OperationType.UPDATE:
                if (
                  !operation.componentId ||
                  !operation.field ||
                  operation.value === undefined
                ) {
                  logger.warn("UPDATE operation missing required fields", {
                    operation,
                  });
                  return false;
                }
                break;

              case OperationType.DELETE_COMPONENT:
              case OperationType.MOVE:
                if (!operation.componentId) {
                  logger.warn("Component operation missing componentId", {
                    operation,
                  });
                  return false;
                }
                break;
            }

            return true;
          } catch (error) {
            logger.error("Operation validation error", { operation, error: error instanceof Error ? error.message : String(error) });
            return false;
          }
        },

        // Detect concurrent operations
        detectConcurrentOperations: (operation: Operation): Operation[] => {
          const { operationQueue } = get();
          const collaborationStore = useCollaborationStore.getState();

          const concurrentOps = operationQueue.filter((queuedOp) => {
            // Skip same operation
            if (queuedOp.id === operation.id) return false;

            // Check if operations are concurrent based on vector clocks
            const op1Vector = operation.vector;
            const op2Vector = queuedOp.vector;

            let isConcurrent = false;
            const allUsers = new Set([
              ...Object.keys(op1Vector),
              ...Object.keys(op2Vector),
            ]);

            for (const userId of allUsers) {
              const time1 = op1Vector[userId] || 0;
              const time2 = op2Vector[userId] || 0;

              if (time1 > 0 && time2 > 0 && time1 !== time2) {
                isConcurrent = true;
                break;
              }
            }

            // Also check if they operate on the same component
            if (
              isConcurrent &&
              operation.componentId === queuedOp.componentId
            ) {
              return true;
            }

            return false;
          });

          logger.debug("Detected concurrent operations", {
            operation: operation.id,
            concurrent: concurrentOps.map((op) => op.id),
          });

          return concurrentOps;
        },

        // Resolve concurrent operations using operational transform
        resolveConcurrentOperations: (operations: Operation[]): Operation[] => {
          if (operations.length <= 1) return operations;

          logger.info("Resolving concurrent operations with OT", {
            operations: operations.map((op) => ({
              id: op.id,
              type: op.type,
              user: op.userId,
            })),
          });

          // Sort operations by timestamp and user priority for deterministic ordering
          const sortedOps = [...operations].sort((a, b) => {
            const timeDiff = (new Date(a.timestamp)).getTime() - (new Date(b.timestamp)).getTime();
            if (timeDiff !== 0) return timeDiff;

            // Use user ID as tie-breaker for deterministic ordering
            return a.userId.localeCompare(b.userId);
          });

          const resolved: Operation[] = [];

          for (let i = 0; i < sortedOps.length; i++) {
            let currentOp: Operation | null = sortedOps[i];

            // Transform current operation against all previously resolved operations
            for (const resolvedOp of resolved) {
              if (!currentOp) break; // Skip if operation was already nullified

              const transformResult = get().resolveOperationConflict(
                currentOp,
                resolvedOp,
              );
              if (transformResult) {
                currentOp = transformResult;
              } else {
                // Conflict requires user resolution - skip this operation
                logger.warn("Operation requires user resolution", {
                  current: currentOp.id,
                  conflictsWith: resolvedOp.id,
                });
                currentOp = null;
                break;
              }
            }

            if (currentOp && get().validateOperationIntegrity(currentOp)) {
              resolved.push(currentOp);
            }
          }

          logger.info("Concurrent operations resolved", {
            input: operations.length,
            resolved: resolved.length,
          });

          return resolved;
        },
      })),
    ),
  );

registerCollaborationBridge(useCollaborationEditorBridge);

// Periodic cleanup
const _collabCleanupIntervalId = setInterval(() => {
  const bridge = useCollaborationEditorBridge.getState();
  bridge.clearOldOperations();
}, 60000); // Every minute

// HMR cleanup: prevent duplicate cleanup intervals on hot reload
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    clearInterval(_collabCleanupIntervalId);
  });
}
